import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { ArrowLeft, FileDown, ShoppingCart } from "lucide-react";
import { Logo, Eyebrow } from "@/components/Reveal";
import ThemeToggle from "@/components/ThemeToggle";
import Seo from "@/components/Seo";
import CableImage from "@/components/CableImage";
import { api } from "@/lib/api";
import { useBasket } from "@/lib/basket";
import { familyBySlug, familyMembers, FAMILIES } from "@/lib/families";
import { useSiteAssets } from "@/lib/siteAssets";

const AV = "Awaiting verification";
const spec = (v, empty = "—") => (v === "?" ? AV : v || empty);

export default function FamilyPage() {
    const { slug: famSlug } = useParams();
    const fam = familyBySlug(famSlug);
    const basket = useBasket();
    const [products, setProducts] = useState(null);
    const [picked, setPicked] = useState({}); // id -> qty (0/falsy = not picked)
    const { images } = useSiteAssets();

    useEffect(() => {
        if (!fam) return;
        api.get("/products", { params: { category: fam.category } })
            .then((res) => setProducts(res.data))
            .catch(() => setProducts([]));
    }, [fam]);

    const members = useMemo(
        () => (products && fam ? familyMembers(products, fam) : []),
        [products, fam]
    );

    if (!fam) {
        return (
            <div className="min-h-screen bg-paper text-ink flex items-center justify-center" data-testid="family-not-found">
                <div className="text-center">
                    <p className="font-display font-semibold text-2xl mb-3">Family not found</p>
                    <Link to="/" className="font-mono text-xs uppercase tracking-[0.2em] text-copper">Back to site</Link>
                </div>
            </div>
        );
    }

    const selectedCount = Object.values(picked).filter((q) => q > 0).length;

    const toggle = (p) =>
        setPicked((prev) => {
            const next = { ...prev };
            if (next[p.id] > 0) delete next[p.id];
            else next[p.id] = 1;
            return next;
        });

    const setQty = (p, qty) =>
        setPicked((prev) => ({ ...prev, [p.id]: Math.max(1, qty || 1) }));

    const toggleAll = () => {
        if (selectedCount === members.length) setPicked({});
        else setPicked(Object.fromEntries(members.map((m) => [m.id, picked[m.id] || 1])));
    };

    const addSelected = () => {
        const chosen = members.filter((m) => picked[m.id] > 0);
        chosen.forEach((m) => basket.add(m, picked[m.id]));
        toast.success(`${chosen.length} item${chosen.length === 1 ? "" : "s"} added to quote basket`, {
            description: chosen.slice(0, 3).map((m) => m.name).join(" · ") + (chosen.length > 3 ? ` · +${chosen.length - 3} more` : ""),
        });
        setPicked({});
    };

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: fam.name,
        category: fam.category,
        brand: fam.brands.map((b) => ({ "@type": "Brand", name: b })),
        material: fam.conductor,
        description: `${fam.name} — ${fam.tag}. ${fam.standard}. Supplied by Roxy Cables, Chickpet, Bengaluru.`,
        url: `https://roxycables.com/family/${fam.slug}`,
    };

    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="family-page">
            <Seo
                title={`${fam.name} in Bengaluru`}
                description={`${fam.name} (${fam.tag}) — ${fam.standard}, ${fam.conductor} conductor. Pick cores and sizes and add to your quote at Roxy Cables, Chickpet, Bengaluru.`}
                path={`/family/${fam.slug}`}
            />
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Helmet>

            <header className="sticky top-0 z-30 backdrop-blur-xl bg-paper/85 border-b border-line">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Logo size={30} />
                        <span className="font-display font-bold tracking-[0.06em] text-lg text-ink hidden sm:block">Roxy Cables</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <ThemeToggle />
                        <Link to="/" data-testid="family-back-link" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mist hover:text-copper transition-colors duration-300">
                            <ArrowLeft size={14} /> All families
                        </Link>
                    </div>
                </div>
            </header>

            <div className="relative h-56 md:h-72 overflow-hidden hud-bg border-b border-line">
                <div className="absolute inset-0 opacity-40">
                    <CableImage slot={fam.slug} images={images} alt={fam.name} className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent dark:from-black/80" />
                <span className="hud-frame" aria-hidden="true" />
                <span className="absolute top-4 right-6 font-display font-bold text-7xl md:text-8xl leading-none text-transparent select-none opacity-50" style={{ WebkitTextStroke: "1.5px var(--copper)" }} aria-hidden="true">
                    {String(FAMILIES.findIndex((f) => f.slug === fam.slug) + 1).padStart(2, "0")}
                </span>
                <div className="absolute bottom-0 inset-x-0 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-8">
                    <Eyebrow className="mb-3 !text-white/70">{fam.tag}</Eyebrow>
                    <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white" data-testid="family-title">
                        {fam.name}
                    </h1>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
                <div className="grid lg:grid-cols-4 gap-4 mb-10" data-testid="family-specs">
                    {[
                        ["Brands", fam.brands.join(" · ")],
                        ["Standard", fam.standard],
                        ["Conductor", fam.conductor],
                        ["Insulation", fam.insulation],
                        ["Armour", fam.armour],
                        ["Voltage", fam.voltage],
                        ["Variants", String(members.length)],
                    ].map(([k, v]) => (
                        <div key={k} className="range-card rounded-md border border-line bg-white p-4 transition-all duration-500">
                            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-dim mb-1">{k}</p>
                            <p className="text-sm text-ink font-medium">{v}</p>
                        </div>
                    ))}
                    <div className="range-card rounded-md border border-line bg-white p-4 transition-all duration-500" data-testid="family-datasheet-slot">
                        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-dim mb-1">Datasheet</p>
                        {fam.datasheet ? (
                            <a href={fam.datasheet} target="_blank" rel="noopener noreferrer" className="text-sm text-copper font-medium flex items-center gap-1.5" data-testid="family-datasheet-link">
                                <FileDown size={13} /> Family datasheet
                            </a>
                        ) : (
                            <p className="text-sm text-dim italic">Available on request with quotation</p>
                        )}
                    </div>
                </div>

                {products === null ? (
                    <div className="rounded-md border border-line bg-white h-64 animate-pulse" />
                ) : members.length === 0 ? (
                    <p className="text-mist" data-testid="family-empty">No variants listed here yet — call us, we stock more than we list.</p>
                ) : (
                    <>
                        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                            <button
                                data-testid="family-select-all"
                                onClick={toggleAll}
                                className="font-mono text-[10px] uppercase tracking-[0.18em] px-4 py-2 border border-line rounded-full text-mist hover:border-copper hover:text-copper transition-colors duration-300"
                            >
                                {selectedCount === members.length ? "Clear all" : "Select all"}
                            </button>
                            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-dim" data-testid="family-selected-count">
                                {selectedCount} of {members.length} selected
                            </p>
                        </div>

                        <div className="rounded-md border border-line bg-white overflow-hidden mb-8 overflow-x-auto pb-16">
                            <table className="w-full min-w-[720px] text-sm" data-testid="family-table">
                                <thead>
                                    <tr className="border-b border-line bg-paper2">
                                        <th className="px-4 py-3.5 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-dim w-10"></th>
                                        <th className="px-4 py-3.5 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-dim">Variant</th>
                                        <th className="px-4 py-3.5 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-dim">Cores</th>
                                        <th className="px-4 py-3.5 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-dim">Size (sqmm)</th>
                                        <th className="px-4 py-3.5 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-dim">Datasheet</th>
                                        <th className="px-4 py-3.5 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-dim w-24">Qty</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {members.map((m) => {
                                        const on = picked[m.id] > 0;
                                        const inBasket = basket?.has(m.id);
                                        return (
                                            <tr
                                                key={m.id}
                                                data-testid={`family-row-${m.sku.toLowerCase()}`}
                                                className={`border-b border-line last:border-b-0 transition-colors duration-200 cursor-pointer ${on ? "bg-copper-light/60" : "hover:bg-paper2/60"}`}
                                                onClick={() => toggle(m)}
                                            >
                                                <td className="px-4 py-3">
                                                    <input
                                                        type="checkbox"
                                                        readOnly
                                                        checked={on}
                                                        tabIndex={-1}
                                                        aria-label={`Select ${m.name}`}
                                                        data-testid={`family-check-${m.sku.toLowerCase()}`}
                                                        className="w-5 h-5 accent-[#2D6BB3] cursor-pointer"
                                                    />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <p className="text-ink font-medium leading-snug">{m.name}</p>
                                                    <p className="font-mono text-[10px] text-dim tracking-wider">{m.sku}</p>
                                                </td>
                                                <td className="px-4 py-3 font-mono text-xs text-mist">{spec(m.cores)}</td>
                                                <td className="px-4 py-3 font-mono text-xs text-mist">{m.sizes.length ? m.sizes.join(" / ") : AV}</td>
                                                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                                    <a
                                                        data-testid={`family-datasheet-${m.sku.toLowerCase()}`}
                                                        href={`${process.env.REACT_APP_BACKEND_URL}/api/products/${m.sku}/datasheet`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        aria-label={`Download datasheet for ${m.name}`}
                                                        className="inline-flex w-8 h-8 rounded-full border border-line items-center justify-center text-mist hover:border-copper hover:text-copper transition-colors duration-300"
                                                    >
                                                        <FileDown size={13} />
                                                    </a>
                                                </td>
                                                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        data-testid={`family-qty-${m.sku.toLowerCase()}`}
                                                        type="number"
                                                        min="1"
                                                        disabled={!on}
                                                        value={picked[m.id] || ""}
                                                        placeholder="—"
                                                        onChange={(e) => setQty(m, parseInt(e.target.value))}
                                                        className="w-20 bg-white border border-line rounded-md px-2.5 py-1.5 text-sm text-ink focus:outline-none focus:border-copper disabled:opacity-40"
                                                    />
                                                </td>
                                                {inBasket && <td className="hidden" data-basket="in" />}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {selectedCount > 0 && (
                        <div className="sticky bottom-4 z-20 flex justify-center pointer-events-none" data-testid="family-quote-bar">
                            <button
                                data-testid="family-add-selected"
                                onClick={addSelected}
                                className="pointer-events-auto rounded-full bg-ink text-white font-mono uppercase tracking-[0.18em] text-[11px] px-8 py-4 shadow-xl hover:bg-copper transition-colors duration-300 flex items-center gap-2"
                            >
                                <ShoppingCart size={14} />
                                Add selected to quote{` (${selectedCount})`}
                            </button>
                        </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
